import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowRight, Crown, Shield, Zap } from 'lucide-react';
import { Button } from './ui/button';

export const LockedOverlay = ({ message = "Upgrade to unlock", feature = "contacts", className = "" }) => {
  const navigate = useNavigate();

  return (
    <div className={`relative ${className}`} data-testid="locked-overlay">
      <div className="absolute inset-0 backdrop-blur-md bg-white/60 z-10 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-[#0A192F]/10 flex items-center justify-center mb-3">
          <Lock className="w-6 h-6 text-[#0A192F]" />
        </div>
        <p className="font-semibold text-[#0A192F] text-sm mb-1">{message}</p>
        <p className="text-xs text-slate-500 mb-4 max-w-xs">
          {feature === 'contacts' && 'Upgrade your membership to view buyer contact details and connect directly.'}
          {feature === 'quotes' && 'Upgrade to Silver or above to send quotes and respond to buy requirements.'}
          {feature === 'rfqs' && 'Upgrade to unlock full RFQ visibility and receive more buyer leads.'}
        </p>
        <Button
          onClick={() => navigate('/membership')}
          className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm h-9 px-5 text-sm font-semibold"
          data-testid="locked-upgrade-btn"
        >
          Upgrade Now
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
};

export const UpgradeBanner = ({ membership = "free", lockedLeads = 0 }) => {
  const navigate = useNavigate();

  if (membership !== 'free') return null;

  return (
    <div className="bg-gradient-to-r from-[#0A192F] to-[#172A46] rounded-sm p-5 mb-6" data-testid="upgrade-banner">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-sm bg-[#EA580C] flex items-center justify-center shrink-0">
            <Crown className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm mb-0.5">Unlock Your Full Potential</h3>
            <p className="text-slate-300 text-xs leading-relaxed">
              {lockedLeads > 0
                ? `You have ${lockedLeads} locked leads waiting. Upgrade to view buyer contacts and send quotes.`
                : 'Upgrade to access buyer contacts, send unlimited quotes, and get featured in search results.'
              }
            </p>
          </div>
        </div>
        <Button
          onClick={() => navigate('/membership')}
          className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm h-9 px-5 text-sm font-semibold whitespace-nowrap shrink-0"
          data-testid="banner-upgrade-btn"
        >
          View Plans
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
};

export const UpgradePopup = ({ isOpen, onClose, trigger = "rfq_view" }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const messages = {
    rfq_view: { title: "Want to respond to this RFQ?", desc: "Upgrade to send quotes and connect with buyers directly." },
    contact_view: { title: "Unlock Buyer Contacts", desc: "Upgrade to view full contact details and reach buyers directly." },
    inquiry_send: { title: "Get More Inquiries", desc: "Premium members receive 5x more buyer inquiries." },
  };

  const msg = messages[trigger] || messages.rfq_view;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="upgrade-popup">
      <div className="bg-white w-full max-w-md mx-4 p-8 animate-slide-up">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-[#EA580C]/10 flex items-center justify-center mx-auto mb-4">
            <Zap className="w-8 h-8 text-[#EA580C]" />
          </div>
          <h3 className="text-xl font-bold text-[#0A192F] mb-2">{msg.title}</h3>
          <p className="text-slate-500 text-sm mb-6">{msg.desc}</p>
          <div className="space-y-3">
            <Button
              onClick={() => { onClose(); navigate('/membership'); }}
              className="w-full bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm h-11 font-semibold"
              data-testid="popup-upgrade-btn"
            >
              <Shield className="w-4 h-4 mr-2" />
              Upgrade Membership
            </Button>
            <Button
              onClick={onClose}
              variant="ghost"
              className="w-full text-slate-500 h-11"
              data-testid="popup-dismiss-btn"
            >
              Maybe Later
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
